import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ActivityBodyInterface } from '@sport-activities/sdk';
import { formatISODuration, intervalToDuration } from 'date-fns';
import { appConfig } from '~/app.config';
import { CheckboxAlignEnum } from '~/component/form/checkbox/enums/checkbox-align.enum';

import { DurationInterface } from '~/component/form/duration/interfaces/duration.interface';
import { Type } from '~/component/form/input/enums/type.enum';
import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { parseISO8601Duration } from '~/core/helpers/date/parse-iso-duration/parse-iso-duration.helper';
import { timeToDate } from '~/core/helpers/date/time-to-date/time-to-date.helper';
import { getRefundTimeLimit } from '~/core/helpers/refund-time-limit/refund-time-limit.helper';
import { FormControlService } from '~/core/services/form/form-control.service';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedPageContainerTipConfigInterface } from '~/organizer/shared/components/shared/page-container/interfaces/tip.config.interface';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ParamethorService } from '~/paramethor/paramethor.service';

@Component({
  selector: 'app-organizer-activity-creation-duration',
  templateUrl: './duration.component.html',
  styleUrls: [
    '../../../../../organizer/shared/components/shared/form-wrapper/form-wrapper.component.scss',
  ],
})
export class OrganizerActivityCreationPageDurationComponent
  extends AbstractOrganizerActivityCreationFormPageDirective
  implements OnInit
{
  protected readonly pageTitleKey =
    'seo.organizer.activity_creation.duration.title';

  public readonly checkboxAlign = CheckboxAlignEnum.Top;
  public readonly timeType = Type.Time;

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly formControlService: FormControlService,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService,
    protected readonly paramethorService: ParamethorService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public get isCourseType(): boolean {
    return this.activityService.activity.isCourse;
  }

  public get isEventType(): boolean {
    return this.activityService.activity.isEvent;
  }

  public get sectionDescriptionKey(): string {
    return this.isCourseType || this.isEventType
      ? 'activity_creation.duration_course.section.description'
      : 'activity_creation.duration.section.description';
  }

  public get timeDurationLabelKey(): string {
    return this.isCourseType || this.isEventType
      ? 'activity_creation.duration.form.time_duration_course.label'
      : 'activity_creation.duration.form.time_duration.label';
  }

  public get tipsConfig(): OrganizerSharedPageContainerTipConfigInterface[] {
    if (!this.isCourseType && !this.isEventType) {
      return [];
    }

    return [
      {
        descriptionKey: 'activity_creation.type.tips.course.description',
        headerKey: 'activity_creation.type.tips.course.header',
      },
    ];
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityBodyInterface> {
    const refundTimeLimit = getRefundTimeLimit(
      this.activityService.activity,
      appConfig.refundLimit
    );

    const bookableUntilTheEnd = formGroupValue.bookableUntilTheEnd;

    if (!this.isCourseType && !this.isEventType) {
      const { hours, minutes } = formGroupValue.timeDuration;

      return {
        duration: formatISODuration({
          hours,
          minutes,
        }),
        refundTimeLimit,
        bookableUntilTheEnd,
      };
    }

    const { hours: courseHours, minutes: courseMinutes } = intervalToDuration({
      start: timeToDate(formGroupValue.courseStartTime),
      end: timeToDate(formGroupValue.courseEndTime),
    });

    return {
      duration: formatISODuration({
        days: formGroupValue.dayDuration - 1 || 0,
        hours: courseHours,
        minutes: courseMinutes,
      }),
      refundTimeLimit,
      bookableUntilTheEnd,
    };
  }

  protected buildValidationConfig(): void {
    const bookableUntilTheEnd = false;

    if (this.isCourseType || this.isEventType) {
      this.validationConfig = {
        dayDuration: ['1', Validators.required],
        courseStartTime: ['', Validators.required],
        courseEndTime: ['', Validators.required],
        bookableUntilTheEnd,
      };
      return;
    }

    const duration: DurationInterface = {
      hours: 1,
      minutes: 0,
    };

    const durationIso = this.activityService.activity.duration;

    if (durationIso) {
      const {
        duration: { hours, minutes },
      } = parseISO8601Duration(durationIso);

      duration.hours = hours;
      duration.minutes = minutes;
    }

    this.validationConfig = {
      timeDuration: [duration, Validators.required],
      bookableUntilTheEnd,
    };
  }

  protected onInitHook(): void {
    this.formGroup.get('timeDuration')?.valueChanges.subscribe((value) => {
      const { hours, minutes } = value;
      if (!hours && !minutes) {
        this.formGroup.get('timeDuration').setErrors({ format: 'true' });
        return;
      }

      this.formGroup.setErrors(null);
    });
  }

  public getErrors(inputName: string): ValidationErrors | null | false {
    const control = this.formGroup.get(inputName);

    return control?.touched && control?.errors;
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    super.submit(formGroupValue);
  }
}
